import type { CardFields, CardMetadata } from '@blinq-types';

export function shouldShowTextField(value: string | undefined, isBeingEdited: boolean): boolean {
  if (isBeingEdited) return true;
  else return !!value && value.length > 0;
}

export function shouldShowAccreditationsField(
  accreditations: string[] | undefined,
  isBeingEdited: boolean,
): boolean {
  if (isBeingEdited) return true;
  else return !!accreditations && accreditations.filter((accreditation) => !!accreditation).length > 0;
}

/**
 * Orders the keys of an object in alphabetical order.
 * @param {unknown} unorderedObject - The object with unordered keys.
 * @returns {unknown} - The object with keys ordered alphabetically.
 */
function orderObjectKeys(unorderedObejct: unknown): unknown {
  // eslint-disable-next-line @typescript-eslint/ban-types
  return Object.keys(unorderedObejct as unknown as Object)
    .sort()
    .reduce((obj, key) => {
      // @ts-expect-error this works but typescript complains
      obj[key] = unorderedObejct[key];
      return obj;
    }, {});
}

/**
 * Checks if two objects are equal by stringifying them and comparing the strings.
 * @param {unknown} obj1 - The first object to compare. Type unknown as typescript complains about the type Object
 * @param {unknown} obj2 - The second object to compare. Type unknown as typescript complains about the type Object
 * @returns {boolean} - Returns true if the objects are equal, false otherwise.
 */
export function areEqual(obj1: unknown, obj2: unknown): boolean {
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;

  try {
    return JSON.stringify(orderObjectKeys(obj1)) === JSON.stringify(orderObjectKeys(obj2));
  } catch {
    return false;
  }
}

export function createCardFieldsObject(partialCardFields?: Partial<CardFields>): CardFields {
  return {
    accreditations: undefined,
    addresses: undefined,
    companyLogoImg: undefined,
    coverPhotoImg: undefined,
    department: undefined,
    emails: undefined,
    fieldOrder: [],
    firstName: '',
    headline: undefined,
    honorificPrefix: '',
    honorificSuffix: '',
    jobTitle: undefined,
    lastName: '',
    layout: undefined,
    linkedAttributes: [],
    maidenName: '',
    middleName: '',
    orgName: undefined,
    phoneNumbers: undefined,
    preferredName: '',
    profilePictureImg: { originalImageUrl: undefined, editedImageUrl: undefined },
    pronouns: '',
    urls: undefined,
    ...partialCardFields,
  };
}

export function createCardMetadataObject(partialCardMetadata?: Partial<CardMetadata>): CardMetadata {
  return {
    activationEmailSentAt: undefined,
    allEmailSignatureFieldsLocked: false,
    allFieldTypesLocked: false,
    bbActivationTimestampMs: undefined,
    blinqMessageEnabled: true,
    cardEditId: '',
    cardId: '',
    cardIsEditable: true,
    cardPresetId: undefined,
    cardTitle: 'Work',
    cardUpdateId: undefined,
    color: '#F45A57',
    colorLocked: false,
    createdBy: undefined,
    creationTimestampMs: 0,
    emailSignature: undefined,
    isInvisible: false,
    isPaused: false,
    layoutImagesLocked: undefined,
    lockedEmailSignatureFields: [],
    lockedFieldTypes: [],
    lockedFields: [],
    logoInQrUrl: undefined,
    logoIsInQr: false,
    orgId: '',
    qrCodeLocked: false,
    recipientEmail: undefined,
    referralLinkEnabled: true,
    referrerCardId: undefined,
    referrerUserId: undefined,
    updatedTimestampMs: 0,
    userId: '',
    userProfileId: undefined,
    whereWeMetEnabled: true,
    ...partialCardMetadata,
  };
}
